import React, { useEffect, useId, useMemo, useState } from "react";
import AdaptiveQueryResultChart from "./charts/AdaptiveQueryResultChart";
import RankingQueryResultVisualization from "./RankingQueryResultVisualization";
import {
  FIELD_TYPES,
  applyFilters,
  buildChartModel,
  buildFilterFields,
  classifyFields,
  detectTemporalField,
  determineChartMode,
  formatFieldLabel,
  formatMetricValue,
  getAutomaticCountryComparison,
  getMeaningfulUniqueValues,
  getMetricAggregationPolicy,
  getMetricFields,
  getRedundantTemporalFields,
  getViewByFields,
  isRoundField,
  isRoundFilterRequired,
  isSupportedViewByField,
  resolveCascadingFilters,
  selectDefaultMetric,
  selectDefaultViewBy,
  validateChartCoordinates,
} from "./utils/visualizationData";
import { getVisualizationEligibility } from "./utils/getVisualizationEligibility";
import { isRankingResponse } from "./utils/rankingVisualization";
import { shouldRenderControlsPanel } from "./utils/visualizationLayout";
import classes from "./GenericQueryResultVisualization.module.css";

const filtersEqual = (left, right) => {
  const leftEntries = Object.entries(left || {});
  const rightEntries = Object.entries(right || {});
  return (
    leftEntries.length === rightEntries.length &&
    leftEntries.every(([field, value]) => String(right?.[field] ?? "") === String(value))
  );
};

const getColumnLabels = (metadata) =>
  (Array.isArray(metadata) ? metadata : []).reduce((labels, column) => {
    if (column?.key && column?.label) labels[column.key] = column.label;
    return labels;
  }, {});

const EmptyState = ({ children, alert = false }) => (
  <div className={classes.empty_state} role={alert ? "alert" : "status"} aria-live="polite">
    {children}
  </div>
);

const ContextFilters = ({ definitions, filters, labels, onChange }) => {
  if (!definitions.length) return null;
  return (
    <div className={classes.filter_grid} aria-label="Visualization context filters">
      {definitions.map(({ field, options }) => (
        <label key={field} className={classes.control_label}>
          <span>{labels[field] || formatFieldLabel(field)}</span>
          <select value={filters[field]} onChange={(event) => onChange(field, event.target.value)}>
            {options.map((option) => (
              <option key={typeof option + ":" + String(option)} value={String(option)}>
                {String(option)}
              </option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
};

const MetricSelector = ({ activeMetric, groupName, labels, metrics, onChange }) => {
  if (metrics.length <= 1) return null;
  return (
    <fieldset className={classes.metric_selector} role="radiogroup">
      <legend>Indicator</legend>
      <div className={classes.metric_pills}>
        {metrics.map((metric) => (
          <label key={metric} className={classes.metric_pill}>
            <input
              type="radio"
              name={groupName}
              value={metric}
              checked={metric === activeMetric}
              onChange={() => onChange(metric)}
            />
            <span>{labels[metric] || formatFieldLabel(metric)}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

function OrdinaryQueryResultVisualization({
  response,
  preferredMetric = null,
  temporalField: configuredTemporalField = null,
}) {
  const rows = useMemo(
    () => (Array.isArray(response?.query_result) ? response.query_result : []),
    [response?.query_result],
  );
  const metricGroupName = useId();
  const eligibility = useMemo(() => getVisualizationEligibility(response), [response]);
  const labels = useMemo(
    () => getColumnLabels(response?.query_result_columns),
    [response?.query_result_columns],
  );
  const [filters, setFilters] = useState({});
  const [viewBy, setViewBy] = useState(null);
  const [activeMetric, setActiveMetric] = useState(null);

  const classifications = useMemo(() => {
    const config = response?.visualization_config || {};
    return classifyFields(rows, {
      temporalField: configuredTemporalField || config.temporal_field || config.temporalField,
      contextFields: config.context_fields || config.contextFields,
      breakdownFields: config.breakdown_fields || config.breakdownFields,
      additiveMetrics: config.additive_metrics || config.additiveMetrics,
      nonAdditiveMetrics: config.non_additive_metrics || config.nonAdditiveMetrics,
    });
  }, [configuredTemporalField, response?.visualization_config, rows]);
  const payloadMetrics = useMemo(
    () => getMetricFields(rows, classifications),
    [classifications, rows],
  );
  const countryComparison = useMemo(
    () =>
      getAutomaticCountryComparison({
        rows,
        classifications,
        metrics: payloadMetrics,
      }),
    [classifications, payloadMetrics, rows],
  );
  const automaticCategoryField = countryComparison.eligible
    ? countryComparison.categoryField
    : null;

  const viewByFields = useMemo(
    () => getViewByFields(rows, classifications, null),
    [classifications, rows],
  );
  const resolvedViewBy = viewByFields.includes(viewBy) ? viewBy : selectDefaultViewBy(viewByFields);
  useEffect(() => {
    if (viewBy !== resolvedViewBy) setViewBy(resolvedViewBy);
  }, [resolvedViewBy, viewBy]);
  const effectiveCategoryField = automaticCategoryField || resolvedViewBy;

  const allFilterFields = useMemo(
    () => buildFilterFields(rows, classifications),
    [classifications, rows],
  );
  const classificationByField = useMemo(
    () => new Map(classifications.map((classification) => [
      classification.field,
      classification,
    ])),
    [classifications],
  );
  const baseFilterFields = useMemo(
    () =>
      allFilterFields.filter((field) => {
        const classification = classificationByField.get(field);
        return (
          field !== effectiveCategoryField &&
          classification?.type === FIELD_TYPES.CONTEXT &&
          !isRoundField(field)
        );
      }),
    [allFilterFields, classificationByField, effectiveCategoryField],
  );
  const baseCascading = useMemo(
    () => resolveCascadingFilters(rows, baseFilterFields, filters, effectiveCategoryField),
    [baseFilterFields, effectiveCategoryField, filters, rows],
  );
  const baseFilteredRows = useMemo(
    () => applyFilters(rows, baseCascading.filters),
    [baseCascading.filters, rows],
  );
  const configuredTemporal =
    configuredTemporalField ||
    response?.visualization_config?.temporal_field ||
    response?.visualization_config?.temporalField;
  const temporalField = useMemo(
    () => detectTemporalField(classifications, configuredTemporal, baseFilteredRows),
    [baseFilteredRows, classifications, configuredTemporal],
  );
  const redundantTemporalFields = useMemo(
    () => getRedundantTemporalFields(classifications, temporalField),
    [classifications, temporalField],
  );
  const roundField = useMemo(
    () => classifications.find(({ field }) => isRoundField(field))?.field || null,
    [classifications],
  );
  const roundFilterRequired = useMemo(
    () => isRoundFilterRequired({
      rows: baseFilteredRows,
      roundField,
      viewBy: effectiveCategoryField,
      temporalField,
    }),
    [baseFilteredRows, effectiveCategoryField, roundField, temporalField],
  );
  const filterFields = useMemo(
    () =>
      allFilterFields.filter((field) => {
        if (field === effectiveCategoryField || field === temporalField) return false;
        if (redundantTemporalFields.includes(field)) return false;
        if (isRoundField(field) && !roundFilterRequired) return false;
        return true;
      }),
    [allFilterFields, effectiveCategoryField, redundantTemporalFields, roundFilterRequired, temporalField],
  );
  const cascading = useMemo(
    () => resolveCascadingFilters(rows, filterFields, filters, [
      effectiveCategoryField,
      temporalField,
      ...redundantTemporalFields,
    ]),
    [effectiveCategoryField, filterFields, filters, redundantTemporalFields, rows, temporalField],
  );
  useEffect(() => {
    if (!filtersEqual(filters, cascading.filters)) setFilters(cascading.filters);
  }, [cascading.filters, filters]);

  const filteredRows = useMemo(
    () => applyFilters(rows, cascading.filters),
    [cascading.filters, rows],
  );
  const metrics = useMemo(
    () => getMetricFields(filteredRows, classifications),
    [classifications, filteredRows],
  );
  const resolvedMetric = metrics.includes(activeMetric)
    ? activeMetric
    : selectDefaultMetric(metrics, preferredMetric);
  useEffect(() => {
    if (activeMetric !== resolvedMetric) setActiveMetric(resolvedMetric);
  }, [activeMetric, resolvedMetric]);

  const mode = determineChartMode({
    rows: filteredRows,
    temporalField,
    viewBy: effectiveCategoryField,
  });
  const aggregationPolicies = useMemo(
    () => Object.fromEntries(
      metrics.map((metric) => [metric, getMetricAggregationPolicy(metric, classifications)]),
    ),
    [classifications, metrics],
  );
  const chartMetrics = useMemo(
    () => (mode === "categorical" ? metrics : resolvedMetric ? [resolvedMetric] : []),
    [metrics, mode, resolvedMetric],
  );
  const ignoredContextFields = [
    ...redundantTemporalFields,
    ...(!roundFilterRequired && roundField ? [roundField] : []),
  ];
  const coordinateValidation = chartMetrics
    .map((metric) =>
      validateChartCoordinates({
        rows: filteredRows,
        classifications,
        viewBy: effectiveCategoryField,
        metric,
        mode,
        temporalField,
        aggregationPolicy: aggregationPolicies[metric],
        ignoredContextFields,
      }),
    )
    .find(({ valid }) => !valid) || { valid: true, unresolvedField: null };
  const chartModel = useMemo(
    () =>
      (mode === "temporal" || effectiveCategoryField) &&
      chartMetrics.length &&
      coordinateValidation.valid
        ? buildChartModel({
            rows: filteredRows,
            mode,
            viewBy: effectiveCategoryField,
            metric: resolvedMetric,
            metrics: chartMetrics,
            temporalField,
            preserveBackendOrder:
              eligibility.ranking || Boolean(automaticCategoryField),
            automaticCountryComparison: Boolean(automaticCategoryField),
            aggregationPolicy: aggregationPolicies[resolvedMetric],
            aggregationPolicies,
            metricLabels: labels,
            tooltipContextFields:
              mode === "temporal" && roundField && !roundFilterRequired ? [roundField] : [],
          })
        : null,
    [
      aggregationPolicies,
      automaticCategoryField,
      chartMetrics,
      coordinateValidation.valid,
      effectiveCategoryField,
      eligibility.ranking,
      filteredRows,
      labels,
      mode,
      resolvedMetric,
      roundField,
      roundFilterRequired,
      temporalField,
    ],
  );
  const contextValues = useMemo(
    () =>
      classifications
        .filter(({ type, field }) => {
          if (
            ![FIELD_TYPES.CONTEXT, FIELD_TYPES.TEMPORAL].includes(type) &&
            !(type === FIELD_TYPES.BREAKDOWN && isSupportedViewByField(field))
          ) return false;
          if (
            field === effectiveCategoryField ||
            field === temporalField ||
            redundantTemporalFields.includes(field)
          ) return false;
          return getMeaningfulUniqueValues(filteredRows, field).length === 1;
        })
        .slice(0, 5)
        .map(({ field }) => ({
          field,
          label: labels[field] || formatFieldLabel(field),
          value: getMeaningfulUniqueValues(filteredRows, field)[0],
        })),
    [classifications, effectiveCategoryField, filteredRows, labels, redundantTemporalFields, temporalField],
  );
  const selectedContext = contextValues.map(({ label, value }) => ({ label, value }));
  const shouldShowViewBy = !automaticCategoryField && viewByFields.length > 0;
  const shouldShowMetricSelector = mode === "temporal" && metrics.length > 1;
  const hasVisibleControls = shouldRenderControlsPanel({
    contextFilterCount: cascading.definitions.length,
    showViewBy: shouldShowViewBy,
    showMetricSelector: shouldShowMetricSelector,
  });

  const changeFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };
  const changeViewBy = (field) => {
    setViewBy(field);
    setFilters((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  if (!eligibility.eligible) return null;


  return (
    <section className={classes.visualization} aria-label="Interactive query result visualization">
      {hasVisibleControls && <div className={classes.controls} data-testid="visualization-controls">
        <ContextFilters
          definitions={cascading.definitions}
          filters={cascading.filters}
          labels={labels}
          onChange={changeFilter}
        />
        {shouldShowViewBy && (
          <label className={classes.control_label}>
            <span>View By</span>
            <select
              aria-label="View By"
              value={resolvedViewBy || ""}
              onChange={(event) => changeViewBy(event.target.value)}
            >
              {viewByFields.map((field) => (
                <option key={field} value={field}>
                  {labels[field] || formatFieldLabel(field)}
                </option>
              ))}
            </select>
          </label>
        )}
        {shouldShowMetricSelector && (
          <MetricSelector
            activeMetric={resolvedMetric}
            groupName={metricGroupName}
            labels={labels}
            metrics={metrics}
            onChange={setActiveMetric}
          />
        )}
      </div>}

      {contextValues.length > 0 && (
        <div className={classes.context_values} aria-label="Selected data context">
          {contextValues.map(({ field, label, value }) => (
            <span key={field}><strong>{label}</strong>{String(value)}</span>
          ))}
        </div>
      )}

      {!filteredRows.length ? (
        <EmptyState>No data available for this selection.</EmptyState>
      ) : !resolvedMetric ? (
        <EmptyState>No numerical indicators are available for this selection.</EmptyState>
      ) : mode !== "temporal" && !effectiveCategoryField ? (
        filteredRows.length === 1 ? (
          <div className={classes.single_value} aria-live="polite">
            <span>{labels[resolvedMetric] || formatFieldLabel(resolvedMetric)}</span>
            <strong>{formatMetricValue(filteredRows[0]?.[resolvedMetric])}</strong>
          </div>
        ) : (
          <EmptyState>No breakdown dimensions are available for this selection.</EmptyState>
        )
      ) : !coordinateValidation.valid ? (
        <EmptyState alert>
          Multiple records map to the same chart point. Select an additional context filter to refine the view.
          {coordinateValidation.unresolvedField && (
            <span className={classes.unresolved_hint}>
              Try {labels[coordinateValidation.unresolvedField] || formatFieldLabel(coordinateValidation.unresolvedField)}.
            </span>
          )}
        </EmptyState>
      ) : !chartModel?.data?.length ? (
        <EmptyState>No data available for this selection.</EmptyState>
      ) : (
        <AdaptiveQueryResultChart
          metricLabel={labels[resolvedMetric] || formatFieldLabel(resolvedMetric)}
          mode={mode}
          model={chartModel}
          selectedContext={selectedContext}
          viewByLabel={
            effectiveCategoryField
              ? labels[effectiveCategoryField] || formatFieldLabel(effectiveCategoryField)
              : "Overall"
          }
        />
      )}
    </section>
  );
}
export default function GenericQueryResultVisualization({
  response,
  preferredMetric = null,
  temporalField = null,
}) {
  const eligibility = getVisualizationEligibility(response);
  if (!eligibility.eligible) return null;
  if (isRankingResponse(response)) {
    return (
      <RankingQueryResultVisualization
        response={response}
        labels={getColumnLabels(response?.query_result_columns)}
      />
    );
  }
  return (
    <OrdinaryQueryResultVisualization
      response={response}
      preferredMetric={preferredMetric}
      temporalField={temporalField}
    />
  );
}